import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import { analyticsAPI } from '../services/api';
import { getBspConversations, getBspConversationMessages } from '../services/tenant.service';
import MetricHint from '../components/common/MetricHint';
import ExplainMetrics from '../components/common/ExplainMetrics';
import { useAuthStore } from '../store/authStore';
import { useLicense } from '../hooks/useLicense';
import { formatDateTime, plural } from '../utils/format';
import {
  ChartBarIcon,
  UserGroupIcon,
  PhoneIcon,
  EnvelopeIcon,
  BuildingOfficeIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ClockIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  TrashIcon,
  XMarkIcon,
  CpuChipIcon,
  ArrowTrendingUpIcon,
  SparklesIcon,
  ArrowTopRightOnSquareIcon,
  DocumentArrowDownIcon,
  EllipsisVerticalIcon,
  PlusIcon,
  ArrowUpTrayIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import ConfirmDeleteDialog from '../components/common/ConfirmDeleteDialog';
import PhoneField, { phoneFieldError } from '../components/common/PhoneField';

// Definitions for the Leads Management dashboard. Kept short and human — the
// slide-over is triaged reading, not a spec doc. When a metric's compute
// changes on the backend, update the calculation line here too.
const LEADS_METRIC_DEFINITIONS = [
  {
    name: 'Leads',
    definition: 'People who left their details with your agent in the dates you picked. The smaller number underneath counts every lead since you started.',
  },
  {
    name: 'Not contacted yet (blue)',
    definition:
      'New leads nobody on your team has contacted. Older than 2 hours is at risk; older than 24 hours is often lost to a faster competitor.',
  },
  {
    name: 'Qualified (green)',
    definition: 'Leads your team marked as genuinely interested and worth moving forward.',
  },
  {
    name: 'Converted (purple)',
    definition: 'Leads your team marked as won: a sale or signed agreement.',
    note: 'Serviti does not detect sales by itself. Mark leads as converted, or send won deals back from your CRM.',
  },
  {
    name: 'Leads by stage',
    definition:
      'Where each lead in the dates you picked stands now. The stages add up to the number of leads in those dates.',
  },
  {
    name: 'Performance Summary',
    definition:
      'Rolled-up KPIs — how fast leads are being handled, hit rate on qualification, and the current bottleneck stage.',
  },
  {
    name: 'Booking Requests',
    definition:
      'Leads in the selected period where the visitor explicitly asked to book a demo or meeting. Higher intent than a generic capture — treat these as hot.',
    calculation: 'Leads in selected period with request_type = "booking"',
  },
  {
    name: 'Not contacted yet',
    definition:
      'New leads still waiting for your team after 2, 24 or 72 hours. If this keeps growing, more people need to see new leads, or see them sooner.',
  },
  {
    name: 'Leads by Priority',
    definition:
      'AI-scored priority for leads in the selected period — Hot / Warm / Cold — using intent signals + past behavior. Unscored means the score job hasn\'t run yet.',
    note: 'Priority score refreshes hourly. Newly captured leads may show as Unscored briefly.',
  },
  {
    name: 'Leads by Request Type',
    definition:
      'What visitors in the selected period actually asked for — pricing, demo, support, general info. Tells you which top-of-funnel content is working.',
  },
  {
    name: 'Leads by Agent',
    definition:
      'Which AI agent captured each lead in the dates you picked. Useful when you run agents on different sites or campaigns.',
  },
];

const statusColors = {
  new: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  contacted: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  qualified: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  converted: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
  lost: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  closed: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
};

const leadStatusOptions = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'converted', label: 'Converted' },
  { value: 'lost', label: 'Lost' },
  { value: 'closed', label: 'Closed' },
];

const formatLeadStatus = (status) => (
  leadStatusOptions.find((option) => option.value === status)?.label || status || 'New'
);

const priorityStyles = {
  hot: { bg: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300', dot: 'bg-red-500', label: 'Hot' },
  warm: { bg: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300', dot: 'bg-amber-500', label: 'Warm' },
  cold: { bg: 'bg-sky-100 text-sky-700 dark:bg-sky-900 dark:text-sky-300', dot: 'bg-sky-500', label: 'Cold' },
};

const requestTypeLabels = {
  callback: 'Callback',
  appointment: 'Appointment',
  site_visit: 'Site Visit',
  campus_visit: 'Campus Visit',
  test_drive: 'Test Drive',
  lab_test: 'Lab Test',
  demo: 'Demo',
  quote: 'Quote',
  reservation: 'Reservation',
};

const followUpLabels = {
  uncontacted: 'Not contacted yet',
  missed_2h: 'Waiting 2+ hours',
  missed_24h: 'Waiting 24+ hours',
};

const followUpCards = [
  { key: 'uncontacted', label: 'Not contacted yet', hint: 'New leads nobody has contacted', tone: 'blue' },
  { key: 'missed_2h', label: 'Waiting 2+ hours', hint: 'Not contacted for 2+ hours', tone: 'amber' },
  { key: 'missed_24h', label: 'Waiting 24+ hours', hint: 'Not contacted for 24+ hours', tone: 'red' },
];

const bookingRequestCards = [
  { key: 'callback', label: 'Callback Requests', hint: 'Visitors asking for a call', tone: 'green' },
  { key: 'appointment', label: 'Appointments', hint: 'Consultation or visit slots', tone: 'blue' },
  { key: 'site_visit', label: 'Site Visits', hint: 'Property/project visits', tone: 'amber' },
  { key: 'campus_visit', label: 'Campus Visits', hint: 'School or campus tours', tone: 'purple' },
];

const AnalyticsPage = () => {
  const { tenantId } = useParams();
  const [searchParams] = useSearchParams();
  const currentUser = useAuthStore((state) => state.user);
  const { isGrowthOrScale } = useLicense();
  const isSuperadminRole = currentUser?.role === 'superadmin' || currentUser?.is_superuser;
  // Quick WhatsApp Note is a Growth+ feature per pricing.html (moved from
  // Scale-only 2026-07-09 to pair with WhatsApp Human Handoff also unlocking
  // at Growth). Superadmins bypass for QA/support. Below-Growth tenants see
  // the button dimmed with an upgrade tooltip.
  const quickWhatsAppNoteEnabled = isGrowthOrScale || isSuperadminRole;
  const isTenantAdmin =
    currentUser?.is_superuser ||
    currentUser?.role === 'superadmin' ||
    currentUser?.tenant_role === 'owner' ||
    currentUser?.tenant_role === 'admin' ||
    (currentUser?.role === 'admin' && currentUser?.tenant_id);
  const preselectedAgent = searchParams.get('agent');
  const didPreselect = useRef(false);
  const [activeTab, setActiveTab] = useState(preselectedAgent ? 'chatbots' : 'overview');
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [leadsTotal, setLeadsTotal] = useState(0);
  const [chatbotAnalytics, setChatbotAnalytics] = useState([]);
  const [selectedChatbot, setSelectedChatbot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showLeadModal, setShowLeadModal] = useState(false);
  // Client-side pagination for the leads table — keeps UI responsive while
  // backend stays at the 100-lead fetch cap. Page clamps to last page if
  // filters shrink the result set.
  const [leadsPage, setLeadsPage] = useState(1);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const LEADS_PAGE_SIZE = 25;
  const [showWhatsAppNote, setShowWhatsAppNote] = useState(false);
  const [waNoteOutcome, setWaNoteOutcome] = useState('');
  const [waNoteStatus, setWaNoteStatus] = useState('');
  const [waNoteSummary, setWaNoteSummary] = useState('');
  const [waNoteSaving, setWaNoteSaving] = useState(false);
  const [bspConvos, setBspConvos] = useState([]);
  const [bspConvosLoading, setBspConvosLoading] = useState(false);
  const [expandedBspConvoId, setExpandedBspConvoId] = useState(null);
  const [bspMessages, setBspMessages] = useState([]);
  const [bspMessagesLoading, setBspMessagesLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [notesDraft, setNotesDraft] = useState('');
  const [editingContact, setEditingContact] = useState(false);
  const [contactDraft, setContactDraft] = useState({ name: '', email: '', phone: '', company: '' });
  const [savingContact, setSavingContact] = useState(false);
  const [contactError, setContactError] = useState(null);
  const [error, setError] = useState(null);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [showManualLeadModal, setShowManualLeadModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [savingManualLead, setSavingManualLead] = useState(false);
  const [importingLeads, setImportingLeads] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [manualLead, setManualLead] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    lead_source: 'manual',
    original_lead_date: '',
    user_query: '',
    notes: '',
    status: 'new',
    assigned_to: '',
    allow_duplicate: false,
  });
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    days: 30,
    priority_tier: '',
    request_type: '',
    follow_up: '',
    sort_by: '',
    // PRD-LEAD-025 campaign attribution filters. Backed by the
    // /analytics/leads/campaigns endpoint that returns the distinct values
    // currently present in this tenant's lead table.
    campaign_source: '',
    campaign_name: '',
  });
  // Distinct campaign values for the filter dropdowns. Populated from
  // /analytics/leads/campaigns on mount + when tenant scope changes.
  const [campaignOptions, setCampaignOptions] = useState({ sources: [], campaigns: [] });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const tenantParams = tenantId ? { tenant_id: tenantId } : {};
      const [statsRes, leadsRes, chatbotRes] = await Promise.all([
        analyticsAPI.getStats(filters.days, tenantParams),
        analyticsAPI.getLeads({
          ...tenantParams,
          status: filters.status || undefined,
          search: filters.search || undefined,
          priority_tier: filters.priority_tier || undefined,
          request_type: filters.request_type || undefined,
          follow_up: filters.follow_up || undefined,
          assigned_to: filters.assigned_to || undefined,
          campaign_source: filters.campaign_source || undefined,
          campaign_name: filters.campaign_name || undefined,
          sort_by: filters.sort_by || undefined,
          days: filters.days,
          limit: 100,
        }),
        analyticsAPI.getChatbotAnalytics(filters.days, tenantParams),
      ]);
      const loadedLeads = leadsRes.data.leads || [];
      setStats(statsRes.data.stats);
      setLeads(loadedLeads);
      setLeadsTotal(leadsRes.data.total || 0);
      // Keep campaign filters useful even when a lead was captured while this
      // page was already open, or if the distinct-campaign endpoint lags/fails.
      setCampaignOptions(prev => {
        const sources = new Set(prev.sources || []);
        const campaigns = new Set(prev.campaigns || []);
        loadedLeads.forEach((lead) => {
          if (lead.campaign_source) sources.add(lead.campaign_source);
          if (lead.campaign_name) campaigns.add(lead.campaign_name);
        });
        return {
          sources: Array.from(sources).sort(),
          campaigns: Array.from(campaigns).sort(),
        };
      });
      const chatbots = chatbotRes.data.chatbots || [];
      setChatbotAnalytics(chatbots);
      // Auto-select: prefer ?agent= query param, else first chatbot
      if (!selectedChatbot && chatbots.length > 0) {
        if (preselectedAgent && !didPreselect.current) {
          const match = chatbots.find(c => c.agent_id === preselectedAgent);
          if (match) setSelectedChatbot(match);
          else setSelectedChatbot(chatbots[0]);
          didPreselect.current = true;
        } else {
          setSelectedChatbot(chatbots[0]);
        }
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [filters.days, filters.status, filters.search, filters.priority_tier, filters.request_type, filters.follow_up, filters.assigned_to, filters.campaign_source, filters.campaign_name, filters.sort_by, tenantId]);

  // Fetch distinct campaign sources / names for the filter dropdowns. Refetch
  // when tenant scope changes; values are cheap (single SELECT DISTINCT) and
  // small (typically <30 sources / <200 campaigns per tenant).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const tenantParams = tenantId ? { tenant_id: tenantId } : {};
        const resp = await analyticsAPI.getLeadCampaigns(tenantParams);
        if (!cancelled && resp?.data) {
          setCampaignOptions({
            sources: resp.data.sources || [],
            campaigns: resp.data.campaigns || [],
          });
        }
      } catch (err) {
        // Non-fatal — dropdowns just stay empty. Most likely cause is a
        // tenant with zero leads carrying campaign attribution yet.
        console.warn('Failed to load campaign filter options:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load team members on mount
  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        const response = await analyticsAPI.getTeamMembers(tenantId ? { tenant_id: tenantId } : {});
        if (response.data.success) {
          setTeamMembers(response.data.team_members);
        }
      } catch (error) {
        console.error('Error loading team members:', error);
      }
    };
    fetchTeamMembers();
  }, [tenantId]);

  // Initialize notes draft when modal opens
  useEffect(() => {
    if (showLeadModal && selectedLead) {
      setNotesDraft(selectedLead.notes || '');
    }
  }, [showLeadModal, selectedLead]);

  // Load BSP/WhatsApp activity for the selected lead when the modal opens.
  // Resets the expanded thread + cached messages so the prior lead's data
  // doesn't flash into view.
  useEffect(() => {
    setExpandedBspConvoId(null);
    setBspMessages([]);
    if (!showLeadModal || !selectedLead) {
      setBspConvos([]);
      return;
    }
    const leadTenantId = tenantId || selectedLead.tenant_id || currentUser?.tenant_id;
    if (!leadTenantId) {
      setBspConvos([]);
      return;
    }
    const fetchConvos = async () => {
      try {
        setBspConvosLoading(true);
        const data = await getBspConversations(leadTenantId, { lead_id: selectedLead.id, limit: 20 });
        setBspConvos(data.conversations || []);
      } catch (err) {
        console.error('Error loading BSP conversations:', err);
        setBspConvos([]);
      } finally {
        setBspConvosLoading(false);
      }
    };
    fetchConvos();
  }, [showLeadModal, selectedLead, tenantId, currentUser?.tenant_id]);

  const handleExpandBspConvo = async (conversationId) => {
    if (expandedBspConvoId === conversationId) {
      setExpandedBspConvoId(null);
      setBspMessages([]);
      return;
    }
    const leadTenantId = tenantId || selectedLead?.tenant_id || currentUser?.tenant_id;
    if (!leadTenantId) return;
    setExpandedBspConvoId(conversationId);
    setBspMessages([]);
    try {
      setBspMessagesLoading(true);
      const data = await getBspConversationMessages(leadTenantId, conversationId, { limit: 200 });
      setBspMessages(data.messages || []);
    } catch (err) {
      console.error('Error loading BSP messages:', err);
      setBspMessages([]);
    } finally {
      setBspMessagesLoading(false);
    }
  };

  const handleViewLead = async (leadId) => {
    try {
      const response = await analyticsAPI.getLead(leadId);
      setSelectedLead(response.data.lead);
      setShowLeadModal(true);
    } catch (error) {
      console.error('Error loading lead:', error);
    }
  };

  const handleUpdateStatus = async (leadId, newStatus) => {
    try {
      await analyticsAPI.updateLeadStatus(leadId, newStatus);
      loadData();
      if (selectedLead && selectedLead.id === leadId) {
        setSelectedLead({ ...selectedLead, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleDeleteLead = async (leadId) => {
    try {
      await analyticsAPI.deleteLead(leadId);
      loadData();
      setShowLeadModal(false);
    } catch (error) {
      console.error('Error deleting lead:', error);
    } finally {
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleAssignLead = async (leadId, userId) => {
    // Optimistically update local state so dropdown doesn't snap back
    const prevLeads = leads;
    setLeads(leads.map(l =>
      l.id === leadId ? { ...l, assigned_to: userId || null } : l
    ));
    if (selectedLead?.id === leadId) {
      setSelectedLead(prev => ({ ...prev, assigned_to: userId || null }));
    }
    setError(null);

    try {
      await analyticsAPI.assignLead(leadId, {
        assigned_to: userId || null,
        assignment_notes: ''
      });
      loadData(); // refresh full data in background
    } catch (error) {
      console.error('Error assigning lead:', error);
      // Revert optimistic update
      setLeads(prevLeads);
      if (selectedLead?.id === leadId) {
        setSelectedLead(prev => ({ ...prev, assigned_to: prevLeads.find(l => l.id === leadId)?.assigned_to }));
      }
      setError('Failed to assign lead. Please try again.');
    }
  };

  const handleSaveNote = async () => {
    if (!selectedLead) return;
    try {
      await analyticsAPI.updateLead(selectedLead.id, { notes: notesDraft }, tenantId ? { tenant_id: tenantId } : {});
      loadData();
      // Update selected lead
      const response = await analyticsAPI.getLead(selectedLead.id);
      setSelectedLead(response.data.lead);
    } catch (error) {
      console.error('Error saving note:', error);
    }
  };

  const startEditContact = () => {
    if (!selectedLead) return;
    setContactError(null);
    setContactDraft({
      name: selectedLead.name || '',
      email: selectedLead.email || '',
      phone: selectedLead.phone || '',
      company: selectedLead.company || '',
    });
    setEditingContact(true);
  };

  const handleSaveContact = async () => {
    if (!selectedLead) return;
    setSavingContact(true);
    setContactError(null);
    try {
      const params = tenantId ? { tenant_id: tenantId } : {};
      await analyticsAPI.updateLead(
        selectedLead.id,
        {
          name: contactDraft.name.trim(),
          email: contactDraft.email.trim(),
          phone: contactDraft.phone.trim(),
          company: contactDraft.company.trim(),
        },
        params,
      );
      const response = await analyticsAPI.getLead(selectedLead.id);
      setSelectedLead(response.data.lead);
      setEditingContact(false);
      loadData();
    } catch (error) {
      setContactError(error?.response?.data?.detail || 'Failed to save contact info');
    } finally {
      setSavingContact(false);
    }
  };

  // Quick WhatsApp Note — captures a post-call/post-chat summary from a
  // non-BSP WhatsApp conversation (personal number, not WATI/Gallabox).
  // Prepends a timestamped "[via WhatsApp]" entry to existing notes and
  // optionally updates lead status. Works on mobile so sales can stamp it
  // right after closing WhatsApp.
  const openWhatsAppNote = () => {
    if (!selectedLead) return;
    if (!quickWhatsAppNoteEnabled) {
      // Non-Scale tenants: don't open the modal at all. A native browser
      // alert would feel abrupt; instead we surface a lightweight toast-
      // style alert. Reuse the window.alert until we add a shared toast
      // in this file (AnalyticsPage doesn't currently import react-hot-toast).
      window.alert('Quick WhatsApp Note is available on the Growth plan. Talk to sales to upgrade.');
      return;
    }
    setWaNoteOutcome('');
    setWaNoteStatus(selectedLead.status || 'contacted');
    setWaNoteSummary('');
    setShowWhatsAppNote(true);
  };

  const handleSaveWhatsAppNote = async () => {
    if (!selectedLead) return;
    const summary = waNoteSummary.trim();
    if (!summary) return;
    setWaNoteSaving(true);
    try {
      const stamp = new Date().toLocaleString('en-IN', {
        day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
      });
      const outcomePrefix = waNoteOutcome ? `${waNoteOutcome}: ` : '';
      const entry = `[via WhatsApp · ${stamp}] ${outcomePrefix}${summary}`;
      const existing = (selectedLead.notes || '').trim();
      const updatedNotes = existing ? `${entry}\n\n${existing}` : entry;

      await analyticsAPI.updateLead(selectedLead.id, { notes: updatedNotes }, tenantId ? { tenant_id: tenantId } : {});

      if (waNoteStatus && waNoteStatus !== selectedLead.status) {
        try {
          await analyticsAPI.updateLeadStatus(selectedLead.id, waNoteStatus);
        } catch (statusErr) {
          console.error('Status update failed; note still saved:', statusErr);
        }
      }

      const refreshed = await analyticsAPI.getLead(selectedLead.id);
      setSelectedLead(refreshed.data.lead);
      setNotesDraft(refreshed.data.lead.notes || '');
      loadData();
      setShowWhatsAppNote(false);
    } catch (error) {
      console.error('Error saving WhatsApp note:', error);
    } finally {
      setWaNoteSaving(false);
    }
  };

  const formatDate = (dateString) => formatDateTime(dateString, 'N/A');

  const exportToPDF = () => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      let yPos = 15;
      
      // Serviti.ai Branding
      doc.setFontSize(12);
      doc.setTextColor(79, 70, 229); // Indigo color
      doc.text('Serviti.ai', pageWidth / 2, yPos, { align: 'center' });
      yPos += 10;
      
      // Title
      doc.setFontSize(20);
      doc.setTextColor(40, 40, 40);
      doc.text('Leads Management Report', pageWidth / 2, yPos, { align: 'center' });
      yPos += 10;
      
      // Date range
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(
        `Generated: ${new Date().toLocaleString()} | Period: ${filters.days === 0 ? 'All time' : `Last ${filters.days} days`}`,
        pageWidth / 2,
        yPos,
        { align: 'center' },
      );
      yPos += 15;
      
      // Stats Summary
      doc.setFontSize(14);
      doc.setTextColor(40, 40, 40);
      doc.text('Summary Statistics', 14, yPos);
      yPos += 8;
      
      doc.setFontSize(10);
      doc.setTextColor(60, 60, 60);
      doc.text(`Total Leads: ${stats?.total_leads || 0}`, 14, yPos);
      yPos += 6;
      doc.text(`Leads with Emails: ${stats?.leads_with_email || 0}`, 14, yPos);
      yPos += 6;
      doc.text(`Leads with Phones: ${stats?.leads_with_phone || 0}`, 14, yPos);
      yPos += 6;
      doc.text(`Leads with Companies: ${stats?.leads_with_company || 0}`, 14, yPos);
      yPos += 6;
      doc.text(`Total Chatbots: ${chatbotAnalytics.length}`, 14, yPos);
      yPos += 12;
      
      // Chatbot Analytics
      if (chatbotAnalytics.length > 0) {
        doc.setFontSize(14);
        doc.setTextColor(40, 40, 40);
        doc.text('Chatbot Analytics', 14, yPos);
        yPos += 8;
        
        doc.setFontSize(9);
        chatbotAnalytics.slice(0, 10).forEach((bot, index) => {
          if (yPos > 270) {
            doc.addPage();
            yPos = 20;
          }
          const botInfo = `${index + 1}. ${bot.agent_name || 'Unknown'} - Conversations: ${bot.total_conversations || 0}, Messages: ${bot.total_messages || 0}, Leads: ${bot.leads_captured || 0}`;
          doc.text(botInfo, 14, yPos);
          yPos += 6;
        });
        
        if (chatbotAnalytics.length > 10) {
          doc.text(`... and ${chatbotAnalytics.length - 10} more chatbots`, 14, yPos);
          yPos += 6;
        }
        yPos += 8;
      }
      
      // Leads List
      if (leads.length > 0) {
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }
        
        doc.setFontSize(14);
        doc.setTextColor(40, 40, 40);
        doc.text('Recent Leads', 14, yPos);
        yPos += 8;
        
        doc.setFontSize(9);
        leads.slice(0, 20).forEach((lead, index) => {
          if (yPos > 270) {
            doc.addPage();
            yPos = 20;
          }
          const leadInfo = `${index + 1}. ${lead.name || 'Anonymous'} | ${lead.email || '-'} | ${lead.phone || '-'} | ${lead.status}`;
          doc.text(leadInfo, 14, yPos);
          yPos += 6;
        });
        
        if (leads.length > 20) {
          doc.text(`... and ${leads.length - 20} more leads`, 14, yPos);
        }
      }
      
      // Save PDF
      doc.save(`analytics-report-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Failed to export PDF');
    }
  };

  const applyFollowUpFilter = (followUp) => {
    setActiveTab('overview');
    setFilters(prev => ({
      ...prev,
      follow_up: prev.follow_up === followUp ? '' : followUp,
      status: followUp ? 'new' : prev.status,
    }));
  };

  const exportLeadsToCSV = async () => {
    setExportingCsv(true);
    setError(null);
    try {
      const response = await analyticsAPI.exportLeadsCsv({
        ...(tenantId ? { tenant_id: tenantId } : {}),
        status: filters.status || undefined,
        search: filters.search || undefined,
        priority_tier: filters.priority_tier || undefined,
        request_type: filters.request_type || undefined,
        follow_up: filters.follow_up || undefined,
        assigned_to: filters.assigned_to || undefined,
        campaign_source: filters.campaign_source || undefined,
        campaign_name: filters.campaign_name || undefined,
        sort_by: filters.sort_by || undefined,
        days: filters.days,
      });
      const disposition = response.headers?.['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match?.[1] || `serviti-leads-${new Date().toISOString().split('T')[0]}.csv`;
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting leads CSV:', error);
      setError('Failed to export leads CSV. Please try again.');
    } finally {
      setExportingCsv(false);
    }
  };

  const resetManualLeadForm = () => {
    setManualLead({
      name: '',
      email: '',
      phone: '',
      company: '',
      lead_source: 'manual',
      original_lead_date: '',
      user_query: '',
      notes: '',
      status: 'new',
      assigned_to: '',
      allow_duplicate: false,
    });
  };

  // A manual lead may legitimately have no phone -- name or email alone is
  // enough. But a phone that IS given has to be usable: it feeds the same
  // WhatsApp alert and BSP push paths as a captured lead, where a number with
  // no country code is simply undeliverable.
  const manualPhoneProblem = manualLead.phone ? phoneFieldError(manualLead.phone) : null;

  const handleCreateManualLead = async (e) => {
    e.preventDefault();
    if (manualPhoneProblem) {
      setError(manualPhoneProblem);
      return;
    }
    setSavingManualLead(true);
    setError(null);
    try {
      // Derive a placeholder agent_name so manual leads don't render as "Unknown"
      // in the leads table. No agent_id is attached — manual leads aren't tied to
      // a real chatbot. Format: "{Tenant Name} Manual Lead", e.g. "Acme Clinic
      // Manual Lead". Tenant name is pulled from the auth context first, then the
      // already-loaded chatbot list, with a generic fallback if neither has it.
      const tenantNameForAgent =
        currentUser?.tenant_name ||
        chatbotAnalytics?.[0]?.tenant_name ||
        'Your business';
      const payload = {
        ...manualLead,
        name: manualLead.name || null,
        email: manualLead.email || null,
        phone: manualLead.phone || null,
        company: manualLead.company || null,
        original_lead_date: manualLead.original_lead_date || null,
        user_query: manualLead.user_query || null,
        notes: manualLead.notes || null,
        assigned_to: manualLead.assigned_to || null,
        agent_name: `${tenantNameForAgent} Manual Lead`,
      };
      await analyticsAPI.createManualLead(payload, tenantId ? { tenant_id: tenantId } : {});
      setShowManualLeadModal(false);
      resetManualLeadForm();
      await loadData();
    } catch (error) {
      console.error('Error creating manual lead:', error);
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string'
        ? detail
        : detail?.message || 'Failed to create manual lead. Please check the details and try again.';
      setError(message);
    } finally {
      setSavingManualLead(false);
    }
  };

  const handleImportCsv = async (e) => {
    e.preventDefault();
    if (!csvFile) {
      setError('Select a CSV file to import.');
      return;
    }
    setImportingLeads(true);
    setImportResult(null);
    setError(null);
    try {
      const response = await analyticsAPI.importLeadsCsv(csvFile, tenantId ? { tenant_id: tenantId } : {});
      setImportResult(response.data);
      setCsvFile(null);
      await loadData();
    } catch (error) {
      console.error('Error importing leads CSV:', error);
      const detail = error.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to import CSV. Please check the file and try again.');
    } finally {
      setImportingLeads(false);
    }
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const leadsTotalPages = Math.max(1, Math.ceil(leads.length / LEADS_PAGE_SIZE));
  const leadsCurrentPage = Math.min(leadsPage, leadsTotalPages);
  const visibleLeads = leads.slice((leadsCurrentPage - 1) * LEADS_PAGE_SIZE, leadsCurrentPage * LEADS_PAGE_SIZE);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <ChartBarIcon className="h-7 w-7" />
            Leads
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Track, qualify, assign, and follow up on every lead captured by your AI agents and WhatsApp.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-3">
          <ExplainMetrics items={LEADS_METRIC_DEFINITIONS} />
          <select
            aria-label="Dates"
            value={filters.days}
            onChange={(e) => setFilters({ ...filters, days: parseInt(e.target.value) })}
            className="min-h-[44px] px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
            <option value={0}>All time</option>
          </select>
          {isTenantAdmin && (
            <button
              type="button"
              onClick={() => {
                setImportResult(null);
                setShowImportModal(true);
              }}
              className="min-h-[44px] px-3 py-2 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
              title="Import past leads from a CSV file"
            >
              <ArrowUpTrayIcon className="w-5 h-5" />
              Import
            </button>
          )}
          <div className="relative">
            <button
              type="button"
              aria-haspopup="menu"
              aria-expanded={exportMenuOpen}
              onClick={() => setExportMenuOpen((open) => !open)}
              className="min-h-[44px] px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
            >
              <DocumentArrowDownIcon className="w-5 h-5" />
              {exportingCsv ? 'Exporting…' : 'Export'}
              <ChevronDownIcon className="w-4 h-4" />
            </button>
            {exportMenuOpen && (
              <div role="menu" className="absolute right-0 z-20 mt-1 w-48 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
                <button
                  type="button"
                  role="menuitem"
                  disabled={exportingCsv}
                  onClick={() => { setExportMenuOpen(false); exportLeadsToCSV(); }}
                  className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                  Spreadsheet (CSV)
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => { setExportMenuOpen(false); exportToPDF(); }}
                  className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                  Report (PDF)
                </button>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => setShowManualLeadModal(true)}
            className="min-h-[44px] px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2 font-semibold"
            title="Add a lead by hand, for example from a phone call"
          >
            <PlusIcon className="w-5 h-5" />
            Add lead
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex overflow-x-auto whitespace-nowrap space-x-4 sm:space-x-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'overview'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <span className="flex items-center gap-2">
              <ChartBarIcon className="h-5 w-5" />
              Overview
            </span>
          </button>
          <button
            onClick={() => setActiveTab('chatbots')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'chatbots'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <span className="flex items-center gap-2">
              <CpuChipIcon className="h-5 w-5" />
              Chatbot Analytics
              <span className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded-full text-xs">
                {chatbotAnalytics.length}
              </span>
            </span>
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' ? (
        <>
          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 inline-flex items-center gap-1">
                  {stats.period_days === 0 ? 'Leads (all time)' : `Leads (last ${stats.period_days} days)`}
                  <MetricHint text="People who left their details in the dates you picked. The line underneath counts every lead since you started." />
                </p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">
                  {stats.period_days === 0 ? stats.total_leads : stats.leads_in_period}
                </p>
              </div>
              <UserGroupIcon className="h-12 w-12 text-blue-500 opacity-50" />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              {stats.period_days === 0 ? 'Every lead since you started' : `${stats.total_leads} all time`}
            </p>
          </div>

          <button
            type="button"
            onClick={() => applyFollowUpFilter('uncontacted')}
            className={`text-left bg-white dark:bg-gray-800 rounded-lg shadow p-6 border transition-colors ${
              filters.follow_up === 'uncontacted'
                ? 'border-blue-400 ring-2 ring-blue-100 dark:ring-blue-900'
                : 'border-transparent hover:border-blue-200 dark:hover:border-blue-800'
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 inline-flex items-center gap-1">
                  Not contacted yet
                  <MetricHint text="New leads nobody on your team has contacted. Click to show only these below. Older than 2 hours is at risk; older than 24 hours is often lost." />
                </p>
                <p className="text-3xl font-bold text-blue-600">{stats.follow_up?.uncontacted || stats.by_status?.new || 0}</p>
              </div>
              <ClockIcon className="h-12 w-12 text-blue-500 opacity-50" />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              Click to filter. {stats.follow_up?.missed_2h || 0} older than 2 hours
            </p>
          </button>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 inline-flex items-center gap-1">
                  Qualified
                  <MetricHint text="Leads your team marked as genuinely interested. The percentage is the share of leads that got at least this far." />
                </p>
                <p className="text-3xl font-bold text-green-600">{stats.by_status?.qualified || 0}</p>
              </div>
              <CheckCircleIcon className="h-12 w-12 text-green-500 opacity-50" />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              {stats.conversion_rate}% of leads got this far
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 inline-flex items-center gap-1">
                  Converted
                  <MetricHint text="Leads your team marked as won: a sale or signed agreement. Mark these yourself in the list below." side="left" />
                </p>
                <p className="text-3xl font-bold text-purple-600">{stats.by_status?.converted || 0}</p>
              </div>
              <FunnelIcon className="h-12 w-12 text-purple-500 opacity-50" />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Won</p>
          </div>
        </div>
      )}

      {/* Lead Pipeline Status */}
      {stats?.by_status && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Leads by stage
              <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                {stats.period_days === 0 ? 'all time' : `last ${stats.period_days} days`}
              </span>
            </h3>
            {filters.status && (
              <button
                type="button"
                onClick={() => setFilters({ ...filters, status: '' })}
                className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400"
              >
                Clear status filter
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {leadStatusOptions.filter((option) => option.value !== 'closed').map((option) => {
              const active = filters.status === option.value;
              const count = stats.by_status[option.value] || 0;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setFilters({ ...filters, status: active ? '' : option.value })}
                  className={`text-left rounded-lg p-4 border transition-colors ${
                    active
                      ? 'border-blue-400 ring-2 ring-blue-100 dark:ring-blue-900 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-transparent bg-gray-50 dark:bg-gray-700 hover:border-gray-200 dark:hover:border-gray-600'
                  }`}
                >
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{count}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{option.label}</p>
                </button>
              );
            })}
            {/* Stages outside the five buttons (older statuses such as closed)
                land here, so the stages always add up to the lead count. */}
            {(() => {
              const shown = leadStatusOptions
                .filter((option) => option.value !== 'closed')
                .reduce((sum, option) => sum + (stats.by_status[option.value] || 0), 0);
              const inWindow = stats.period_days === 0 ? stats.total_leads : stats.leads_in_period;
              const other = Math.max(0, (Number(inWindow) || 0) - shown);
              return other > 0 ? (
                <div role="group" aria-label={`Other stages: ${other}`} className="rounded-lg p-4 bg-gray-50 dark:bg-gray-700">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{other}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Other</p>
                </div>
              ) : null;
            })()}
          </div>
        </div>
      )}

      {/* Performance Summary */}
      {stats?.performance_summary && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Performance Summary</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {stats.period_days === 0 ? 'All time' : `Last ${stats.period_days} days`}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                label: 'Leads Captured',
                value: stats.performance_summary.period_leads || 0,
                hint: `${stats.performance_summary.after_hours_leads || 0} after hours`,
                icon: UserGroupIcon,
                tone: 'text-blue-600',
                tooltip: 'Total leads captured in the selected window. "After hours" = leads captured outside your business hours, where the AI agent was the only thing answering.',
              },
              {
                label: 'High Intent',
                value: stats.performance_summary.hot_leads || 0,
                hint: `${stats.performance_summary.qualified_leads || 0} qualified or converted`,
                icon: SparklesIcon,
                tone: 'text-red-600',
                tooltip: 'Leads the AI scored as hot (priority score 70+). Subtitle shows how many your team has actually moved forward (qualified or converted) — gap between hot and qualified = follow-up risk.',
              },
              {
                label: 'Booking Requests',
                value: stats.performance_summary.booking_requests || 0,
                hint: `${stats.performance_summary.callback_requests || 0} callbacks, ${stats.performance_summary.visit_requests || 0} visits`,
                icon: PhoneIcon,
                tone: 'text-green-600',
                tooltip: 'Leads who asked for something concrete during the conversation: a callback, site visit, demo, appointment, or test drive. These are your highest-intent prospects.',
              },
              {
                label: 'Not contacted yet',
                value: stats.performance_summary.uncontacted_leads || 0,
                hint: `${stats.performance_summary.missed_24h_leads || 0} waiting 24+ hours`,
                icon: ClockIcon,
                tone: 'text-amber-600',
                followUp: 'uncontacted',
                tooltip: 'Leads no one on your team has marked as contacted yet. "Missed 24+ hours" = leads from yesterday or earlier still waiting — most likely already lost to a competitor who replied faster.',
              },
            ].map((item) => {
              const Icon = item.icon;
              const active = item.followUp && filters.follow_up === item.followUp;
              return (
                <button
                  type="button"
                  key={item.label}
                  onClick={() => item.followUp && applyFollowUpFilter(item.followUp)}
                  className={`text-left bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border transition-colors ${
                    item.followUp
                      ? active
                        ? 'border-blue-400 ring-2 ring-blue-100 dark:ring-blue-900'
                        : 'border-transparent hover:border-gray-200 dark:hover:border-gray-600'
                      : 'border-transparent cursor-default'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 inline-flex items-center gap-1">
                        {item.label}
                        {item.tooltip && <MetricHint text={item.tooltip} />}
                      </p>
                      <p className={`text-3xl font-bold ${item.tone}`}>{item.value}</p>
                    </div>
                    <Icon className={`h-10 w-10 opacity-50 ${item.tone}`} />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">{item.hint}</p>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Booking Requests */}
      {stats?.by_request_type && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Booking Requests</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {bookingRequestCards.map((item) => {
              const active = filters.request_type === item.key;
              const count = stats.by_request_type[item.key] || 0;
              const toneClass = item.tone === 'green'
                ? 'text-green-600'
                : item.tone === 'amber'
                  ? 'text-amber-600'
                  : item.tone === 'purple'
                    ? 'text-purple-600'
                    : 'text-blue-600';
              return (
                <button
                  key={item.key}
                  onClick={() => setFilters({ ...filters, request_type: active ? '' : item.key })}
                  className={`text-left bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border transition-colors ${
                    active
                      ? 'border-blue-400 ring-2 ring-blue-100 dark:ring-blue-900'
                      : 'border-transparent hover:border-gray-200 dark:hover:border-gray-600'
                  }`}
                >
                  <p className={`text-3xl font-bold ${toneClass}`}>{count}</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">{item.label}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{item.hint}</p>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Follow-up Queue */}
      {stats?.follow_up && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Not contacted yet</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Filter leads that still need a first sales touch.
              </p>
            </div>
            {filters.follow_up && (
              <button
                type="button"
                onClick={() => setFilters({ ...filters, follow_up: '', status: '' })}
                className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400"
              >
                Clear queue filter
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {followUpCards.map((item) => {
              const active = filters.follow_up === item.key;
              const toneClass = item.tone === 'red'
                ? 'text-red-600'
                : item.tone === 'amber'
                  ? 'text-amber-600'
                  : 'text-blue-600';
              return (
                <button
                  key={item.key}
                  onClick={() => applyFollowUpFilter(item.key)}
                  className={`text-left bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border transition-colors ${
                    active
                      ? 'border-blue-400 ring-2 ring-blue-100 dark:ring-blue-900'
                      : 'border-transparent hover:border-gray-200 dark:hover:border-gray-600'
                  }`}
                >
                  <p className={`text-3xl font-bold ${toneClass}`}>{stats.follow_up[item.key] || 0}</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">{item.label}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{item.hint}</p>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Leads by Priority */}
      {stats?.by_priority && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Leads by Priority</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {['hot', 'warm', 'cold'].map((tier) => {
              const style = priorityStyles[tier];
              const count = stats.by_priority[tier] || 0;
              return (
                <div key={tier} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <span className={`h-2.5 w-2.5 rounded-full ${style.dot}`} />
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{style.label}</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{count}</p>
                </div>
              );
            })}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <span className="h-2.5 w-2.5 rounded-full bg-gray-400" />
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Unscored</span>
              </div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.by_priority.unscored || 0}</p>
            </div>
          </div>
        </div>
      )}

      {/* Leads by Request Type */}
      {stats?.by_request_type && Object.keys(stats.by_request_type).length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Leads by Request Type</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {Object.entries(stats.by_request_type).map(([type, count]) => (
              <button
                key={type}
                onClick={() => setFilters({ ...filters, request_type: filters.request_type === type ? '' : type })}
                className={`bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center border transition-colors ${
                  filters.request_type === type
                    ? 'border-blue-400 ring-2 ring-blue-100 dark:ring-blue-900'
                    : 'border-transparent hover:border-gray-200 dark:hover:border-gray-600'
                }`}
              >
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{count}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                  {requestTypeLabels[type] || type.replace(/_/g, ' ')}
                </p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Leads by Agent */}
      {stats?.by_agent && stats.by_agent.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Leads by Agent</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {stats.by_agent.map((item, idx) => (
              <div key={idx} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{item.count}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{item.agent}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center justify-between">
          <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
          <button
            onClick={() => setError(null)}
            className="text-red-400 hover:text-red-600 dark:hover:text-red-200"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Filters and Leads Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
            Showing {leadsTotal} {leadsTotal === 1 ? 'lead' : 'leads'} matching the current filters in {filters.days === 0 ? 'all time' : `the last ${filters.days} days`}
            {stats ? ` · ${stats.total_leads} all-time` : ''}
          </p>
          {filters.follow_up && (
            <div className="mb-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-900 dark:bg-amber-900/20">
              <div>
                <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                  Showing {followUpLabels[filters.follow_up] || 'follow-up'} leads
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  These leads are still marked new and have not been contacted.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setFilters({ ...filters, follow_up: '', status: '' })}
                className="text-sm font-medium text-amber-800 hover:text-amber-950 dark:text-amber-200"
              >
                Clear filter
              </button>
            </div>
          )}
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search leads..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">All Statuses</option>
              {leadStatusOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <select
              value={filters.priority_tier}
              onChange={(e) => setFilters({ ...filters, priority_tier: e.target.value })}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">All Priorities</option>
              <option value="hot">Hot</option>
              <option value="warm">Warm</option>
              <option value="cold">Cold</option>
            </select>
            <select
              value={filters.request_type}
              onChange={(e) => setFilters({ ...filters, request_type: e.target.value })}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">All Requests</option>
              {Object.entries(requestTypeLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            <select
              value={filters.assigned_to}
              onChange={(e) => setFilters({ ...filters, assigned_to: e.target.value })}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">All Assignees</option>
              <option value="unassigned">Unassigned</option>
              {teamMembers.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.full_name || member.email || member.username || 'Unknown'}
                </option>
              ))}
            </select>
            {currentUser?.id && (
              <button
                type="button"
                onClick={() => setFilters({
                  ...filters,
                  assigned_to: filters.assigned_to === currentUser.id ? '' : currentUser.id,
                })}
                className={`px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${
                  filters.assigned_to === currentUser.id
                    ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900 dark:border-blue-600 dark:text-blue-300'
                    : 'border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700'
                }`}
                title="Show only leads assigned to you"
              >
                {filters.assigned_to === currentUser.id ? 'My Leads (on)' : 'My Leads'}
              </button>
            )}
            {/* PRD-LEAD-025 — campaign attribution filters. Populated from
                /analytics/leads/campaigns (distinct values currently on this
                tenant's leads). Empty array → dropdown shows just the "All"
                option, which is the correct UX for a tenant whose leads
                haven't carried any campaign attribution yet. */}
            <select
              value={filters.campaign_source}
              onChange={(e) => setFilters({ ...filters, campaign_source: e.target.value })}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              title="Filter by traffic source (WATI, Google, SMS, etc.)"
            >
              <option value="">All Sources</option>
              <option value="unattributed">Unattributed (no source)</option>
              {(campaignOptions.sources || []).map(src => (
                <option key={src} value={src}>{src}</option>
              ))}
            </select>
            <select
              value={filters.campaign_name}
              onChange={(e) => setFilters({ ...filters, campaign_name: e.target.value })}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              title="Filter by campaign name"
            >
              <option value="">All Campaigns</option>
              {(campaignOptions.campaigns || []).map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
            <select
              value={filters.follow_up}
              onChange={(e) => setFilters({ ...filters, follow_up: e.target.value })}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">All Follow-up</option>
              {Object.entries(followUpLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            <button
              onClick={() => setFilters({ ...filters, sort_by: filters.sort_by === 'priority' ? '' : 'priority' })}
              className={`px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${
                filters.sort_by === 'priority'
                  ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900 dark:border-blue-600 dark:text-blue-300'
                  : 'border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700'
              }`}
            >
              {filters.sort_by === 'priority' ? 'Sorted by Priority' : 'Sort by Priority'}
            </button>
          </div>
        </div>

        {/* Phones: cards with the two things an owner does from a phone --
            reach the lead, and mark them contacted. The table needed 1,100px. */}
        <ul className="md:hidden divide-y divide-gray-200 dark:divide-gray-700" aria-label="Leads">
          {visibleLeads.length === 0 ? (
            <li className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
              No leads yet. They appear here when visitors leave their details in a chat.
            </li>
          ) : visibleLeads.map((lead) => {
            const digits = String(lead.phone || '').replace(/[^\d]/g, '');
            const priority = priorityStyles[lead.priority_tier];
            return (
              <li key={lead.id} className="px-4 py-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900 dark:text-white">{lead.name || 'Anonymous'}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{lead.phone || lead.email || 'No contact details'}</p>
                  </div>
                  {priority && (
                    <span className={`shrink-0 inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full ${priority.bg}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${priority.dot}`} />
                      {priority.label}
                    </span>
                  )}
                </div>
                <p className="mt-1 text-sm text-gray-700 dark:text-gray-200 line-clamp-2">{lead.user_query || 'No question recorded'}</p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {lead.request_type ? `${lead.request_label || requestTypeLabels[lead.request_type] || lead.request_type.replace(/_/g, ' ')} · ` : ''}
                  {formatLeadStatus(lead.status)} · {formatDate(lead.created_at)}
                </p>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {digits ? (
                    <a
                      href={`https://wa.me/${digits}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-green-600 text-sm font-semibold text-green-700 dark:text-green-300"
                    >
                      WhatsApp
                    </a>
                  ) : lead.email ? (
                    <a
                      href={`mailto:${lead.email}`}
                      className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 dark:text-gray-200"
                    >
                      Email
                    </a>
                  ) : <span />}
                  {lead.status === 'new' && !lead.is_contacted ? (
                    <button
                      type="button"
                      onClick={() => handleUpdateStatus(lead.id, 'contacted')}
                      className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-indigo-600 text-sm font-semibold text-white"
                    >
                      Mark contacted
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleViewLead(lead.id)}
                      className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 dark:text-gray-200"
                    >
                      Details
                    </button>
                  )}
                </div>
                {digits && (
                  <a href={`tel:+${digits}`} className="mt-2 inline-flex min-h-[44px] items-center text-sm font-semibold text-indigo-700 dark:text-indigo-300">
                    Call {lead.phone}
                  </a>
                )}
              </li>
            );
          })}
        </ul>

        {/* Leads Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Contact
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Query
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Request
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Agent
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Priority
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Assigned To
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Date
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {leads.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    No leads yet. They appear here when visitors leave their details in a chat.
                  </td>
                </tr>
              ) : (
                visibleLeads.map((lead) => (
                  <tr key={lead.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-4">
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {lead.name || 'Anonymous'}
                        </span>
                        {lead.email && (
                          <span className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                            <EnvelopeIcon className="h-3 w-3" />
                            {lead.email}
                          </span>
                        )}
                        {lead.phone && (
                          <span className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                            <PhoneIcon className="h-3 w-3" />
                            {lead.phone}
                          </span>
                        )}
                        {lead.company && (
                          <span className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                            <BuildingOfficeIcon className="h-3 w-3" />
                            {lead.company}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-sm text-gray-900 dark:text-white line-clamp-2">
                        {lead.user_query || 'N/A'}
                      </p>
                      <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-1">
                        <ChatBubbleLeftRightIcon className="h-3 w-3" />
                        {plural(lead.messages_count, 'message')}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      {lead.request_type ? (
                        <span className="inline-flex items-center text-xs font-medium px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300">
                          {lead.request_label || requestTypeLabels[lead.request_type] || lead.request_type.replace(/_/g, ' ')}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">---</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-sm text-gray-900 dark:text-white">
                        {lead.agent_name || 'Unknown'}
                      </span>
                      {(lead.campaign_source || lead.campaign_name) && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {lead.campaign_source && (
                            <span className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300">
                              {lead.campaign_source}
                            </span>
                          )}
                          {lead.campaign_name && (
                            <span className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                              {lead.campaign_name}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      {lead.priority_tier && priorityStyles[lead.priority_tier] ? (
                        <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full ${priorityStyles[lead.priority_tier].bg}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${priorityStyles[lead.priority_tier].dot}`} />
                          {priorityStyles[lead.priority_tier].label} ({lead.priority_score})
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">---</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      {isTenantAdmin ? (
                        <select
                          value={lead.assigned_to || ''}
                          onChange={(e) => handleAssignLead(lead.id, e.target.value)}
                          className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                        >
                          <option value="">Unassigned</option>
                          {teamMembers.map((member) => (
                            <option key={member.id} value={member.id}>
                              {member.full_name || member.email || member.username || 'Unknown'}
                            </option>
                          ))}
                        </select>
                      ) : (
                        // Non-admin tenant members see the assignee as read-only
                        // text — backend rejects assign with 403 even if the UI
                        // were bypassed. (See routes/analytics.py::assign_lead.)
                        <span className="text-xs text-gray-700 dark:text-gray-300">
                          {(() => {
                            const owner = teamMembers.find(m => m.id === lead.assigned_to);
                            return owner
                              ? (owner.full_name || owner.email || owner.username || 'Assigned')
                              : 'Unassigned';
                          })()}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <select
                        value={lead.status}
                        onChange={(e) => handleUpdateStatus(lead.id, e.target.value)}
                        className={`text-xs font-medium px-2 py-1 rounded-full border-0 ${statusColors[lead.status] || statusColors.new}`}
                      >
                        {leadStatusOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                      {lead.follow_up_status && (
                        <p className={`text-xs mt-1 ${
                          lead.follow_up_status === 'missed_24h'
                            ? 'text-red-600 dark:text-red-400'
                            : lead.follow_up_status === 'missed_2h'
                              ? 'text-amber-600 dark:text-amber-400'
                            : 'text-amber-600 dark:text-amber-400'
                        }`}>
                          {followUpLabels[lead.follow_up_status] || 'Needs follow-up'}
                          {Number.isFinite(lead.age_hours) ? ` · ${lead.age_hours}h old` : ''}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(lead.created_at)}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex items-center justify-end gap-3">
                        <button
                          type="button"
                          onClick={() => handleViewLead(lead.id)}
                          title={lead.notes || undefined}
                          className="text-sm text-gray-600 hover:text-gray-900 hover:underline dark:text-gray-300"
                        >
                          {lead.notes ? 'Note' : 'Add note'}
                        </button>
                        <button
                          onClick={() => handleViewLead(lead.id)}
                          className="text-sm text-blue-600 hover:text-blue-800 hover:underline dark:text-blue-400"
                        >
                          View Details
                        </button>
                        {lead.status === 'new' && !lead.is_contacted && (
                          <button
                            onClick={() => handleUpdateStatus(lead.id, 'contacted')}
                            className="text-sm text-green-600 hover:text-green-800 hover:underline dark:text-green-400"
                          >
                            Mark Contacted
                          </button>
                        )}
                        {/* More-actions menu is the only place Delete Lead is exposed.
                            Restricted to tenant admins + superadmins; non-admin members
                            see nothing in this slot. Backend also enforces 403 on
                            DELETE /leads/{id} as defense-in-depth (see routes/analytics.py). */}
                        {isTenantAdmin && (
                          <div className="relative">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const menu = e.currentTarget.nextElementSibling;
                                menu.classList.toggle('hidden');
                                // Close on outside click
                                const close = (ev) => { if (!menu.contains(ev.target)) { menu.classList.add('hidden'); document.removeEventListener('click', close); } };
                                setTimeout(() => document.addEventListener('click', close), 0);
                              }}
                              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                              title="More actions"
                            >
                              <EllipsisVerticalIcon className="h-5 w-5" />
                            </button>
                            <div className="hidden absolute right-0 mt-1 w-36 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                              <button
                                onClick={() => { setItemToDelete(lead); setDeleteDialogOpen(true); }}
                                className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md"
                              >
                                <TrashIcon className="h-4 w-4" />
                                Delete Lead
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {leads.length > LEADS_PAGE_SIZE && (() => {
          const totalPages = Math.max(1, Math.ceil(leads.length / LEADS_PAGE_SIZE));
          const currentPage = Math.min(leadsPage, totalPages);
          return (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 px-4 py-3 border-t border-gray-200 dark:border-gray-700 text-sm">
              <span className="text-gray-500 dark:text-gray-400 text-center sm:text-left">
                Page {currentPage} of {totalPages} · Showing {(currentPage - 1) * LEADS_PAGE_SIZE + 1}–
                {Math.min(currentPage * LEADS_PAGE_SIZE, leads.length)} of {leads.length} leads
              </span>
              <div className="flex gap-2 justify-center">
                <button
                  type="button"
                  onClick={() => setLeadsPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Prev
                </button>
                <button
                  type="button"
                  onClick={() => setLeadsPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                  className="px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          );
        })()}
      </div>
        </>
      ) : (
        /* Chatbot Analytics Tab */
        <div className="space-y-6">
          {/* Chatbot Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Total Chatbots</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">{chatbotAnalytics.length}</p>
                </div>
                <CpuChipIcon className="h-12 w-12 text-blue-500 opacity-50" />
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                {chatbotAnalytics.filter(c => c.is_active).length} active
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Total Conversations</p>
                  <p className="text-3xl font-bold text-blue-600">
                    {chatbotAnalytics.reduce((sum, c) => sum + c.stats.total_conversations, 0)}
                  </p>
                </div>
                <ChatBubbleLeftRightIcon className="h-12 w-12 text-blue-500 opacity-50" />
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                {filters.days === 0 ? 'All time' : `In last ${filters.days} days`}
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Total Messages</p>
                  <p className="text-3xl font-bold text-green-600">
                    {chatbotAnalytics.reduce((sum, c) => sum + c.stats.total_messages, 0)}
                  </p>
                </div>
                <ArrowTrendingUpIcon className="h-12 w-12 text-green-500 opacity-50" />
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Across all chatbots
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Leads Captured</p>
                  <p className="text-3xl font-bold text-purple-600">
                    {chatbotAnalytics.reduce((sum, c) => sum + c.stats.leads_captured, 0)}
                  </p>
                </div>
                <SparklesIcon className="h-12 w-12 text-purple-500 opacity-50" />
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                {chatbotAnalytics.reduce((sum, c) => sum + c.stats.qualified_leads, 0)} qualified
              </p>
            </div>
          </div>

          {/* Chatbot List and Details */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Chatbot List */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Chatbots</h3>
              </div>
              <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-[500px] overflow-y-auto">
                {chatbotAnalytics.length === 0 ? (
                  <p className="p-4 text-center text-gray-500 dark:text-gray-400">
                    No chatbots found. Create agents in the Agents page.
                  </p>
                ) : (
                  chatbotAnalytics.map((chatbot) => (
                    <div
                      key={chatbot.agent_id}
                      className={`w-full p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                        selectedChatbot?.agent_id === chatbot.agent_id
                          ? 'bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-500'
                          : ''
                      }`}
                    >
                      <button
                        onClick={() => setSelectedChatbot(chatbot)}
                        className="w-full text-left"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              chatbot.is_active 
                                ? 'bg-gradient-to-r from-blue-500 to-purple-500' 
                                : 'bg-gray-400'
                            }`}>
                              <span className="text-white font-bold">
                                {chatbot.agent_name?.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">
                                {chatbot.agent_name}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {chatbot.model} • {chatbot.provider}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-gray-900 dark:text-white">
                              {chatbot.stats.total_conversations}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">conversations</p>
                          </div>
                        </div>
                      </button>
                      <a
                        href={`/analytics/chatbot/${encodeURIComponent(chatbot.agent_name)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                        Open Analytics Page
                      </a>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Selected Chatbot Details */}
            <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg shadow">
              {selectedChatbot ? (
                <>
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          selectedChatbot.is_active 
                            ? 'bg-gradient-to-r from-blue-500 to-purple-500' 
                            : 'bg-gray-400'
                        }`}>
                          <span className="text-white font-bold text-lg">
                            {selectedChatbot.agent_name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {selectedChatbot.agent_name}
                          </h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {selectedChatbot.model} • {selectedChatbot.provider}
                            {selectedChatbot.use_rag && ' • RAG enabled'}
                          </p>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        selectedChatbot.is_active
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                      }`}>
                        {selectedChatbot.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">
                          {selectedChatbot.stats.total_conversations}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Conversations</p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">
                          {selectedChatbot.stats.total_messages}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Total Messages</p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">
                          {selectedChatbot.stats.avg_messages_per_conversation}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Avg per Conv</p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">
                          {selectedChatbot.stats.leads_captured}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Leads Captured</p>
                      </div>
                    </div>

                    {/* Message Breakdown */}
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                        Message Breakdown
                      </h4>
                      <div className="flex gap-4">
                        <div className="flex-1 bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3">
                          <p className="text-lg font-bold text-blue-600 dark:text-blue-400">
                            {selectedChatbot.stats.user_messages}
                          </p>
                          <p className="text-xs text-blue-500 dark:text-blue-400">User Messages</p>
                        </div>
                        <div className="flex-1 bg-green-50 dark:bg-green-900/30 rounded-lg p-3">
                          <p className="text-lg font-bold text-green-600 dark:text-green-400">
                            {selectedChatbot.stats.assistant_messages}
                          </p>
                          <p className="text-xs text-green-500 dark:text-green-400">Bot Responses</p>
                        </div>
                        <div className="flex-1 bg-purple-50 dark:bg-purple-900/30 rounded-lg p-3">
                          <p className="text-lg font-bold text-purple-600 dark:text-purple-400">
                            {selectedChatbot.stats.conversion_rate}%
                          </p>
                          <p className="text-xs text-purple-500 dark:text-purple-400">Lead Conversion</p>
                        </div>
                      </div>
                    </div>

                    {/* Daily Activity Chart */}
                    {selectedChatbot.stats.daily_activity && selectedChatbot.stats.daily_activity.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                          Daily Activity (Last {selectedChatbot.stats.activity_days || 14} days)
                        </h4>
                        <div className="flex items-end gap-1 h-32 bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                          {selectedChatbot.stats.daily_activity.map((day, idx) => {
                            const maxConvs = Math.max(...selectedChatbot.stats.daily_activity.map(d => d.conversations), 1);
                            const height = (day.conversations / maxConvs) * 100;
                            return (
                              <div key={idx} className="flex-1 flex flex-col items-center justify-end h-full">
                                <div
                                  className="w-full bg-blue-500 rounded-t transition-all hover:bg-blue-600"
                                  style={{ height: `${Math.max(height, 4)}%` }}
                                  title={`${day.date}: ${day.conversations} conversations`}
                                />
                                <span className="text-[8px] text-gray-400 mt-1 transform -rotate-45">
                                  {new Date(day.date).getDate()}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                  Select a chatbot to view detailed analytics
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Manual Lead Modal */}
      {showManualLeadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleCreateManualLead}>
              <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Add Lead</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Add an offline, phone, WhatsApp, broker, flyer, or historical lead.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowManualLeadModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Name</span>
                  <input
                    type="text"
                    value={manualLead.name}
                    onChange={(e) => setManualLead({ ...manualLead, name: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Phone</span>
                  <PhoneField
                    value={manualLead.phone}
                    onChange={(next) => setManualLead({ ...manualLead, phone: next })}
                    className="mt-1"
                  />
                  {manualPhoneProblem && (
                    <span className="mt-1 block text-xs text-red-500">{manualPhoneProblem}</span>
                  )}
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Email</span>
                  <input
                    type="email"
                    value={manualLead.email}
                    onChange={(e) => setManualLead({ ...manualLead, email: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Company / Project / School</span>
                  <input
                    type="text"
                    value={manualLead.company}
                    onChange={(e) => setManualLead({ ...manualLead, company: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Lead Source</span>
                  <select
                    value={manualLead.lead_source}
                    onChange={(e) => setManualLead({ ...manualLead, lead_source: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="manual">Manual</option>
                    <option value="flyer">Flyer / QR</option>
                    <option value="walk_in">Walk-in</option>
                    <option value="phone_call">Phone call</option>
                    <option value="whatsapp">WhatsApp</option>
                    <option value="broker">Broker</option>
                    <option value="event">Event</option>
                    <option value="portal">Portal</option>
                    <option value="other">Other</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Original Lead Date</span>
                  <input
                    type="date"
                    value={manualLead.original_lead_date}
                    onChange={(e) => setManualLead({ ...manualLead, original_lead_date: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Status</span>
                  <select
                    value={manualLead.status}
                    onChange={(e) => setManualLead({ ...manualLead, status: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {leadStatusOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Assign To</span>
                  <select
                    value={manualLead.assigned_to}
                    onChange={(e) => setManualLead({ ...manualLead, assigned_to: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Unassigned</option>
                    {teamMembers.map((member) => (
                      <option key={member.id} value={member.id}>
                        {member.full_name || member.email}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block md:col-span-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Interest / Requirement</span>
                  <input
                    type="text"
                    value={manualLead.user_query}
                    onChange={(e) => setManualLead({ ...manualLead, user_query: e.target.value })}
                    placeholder="e.g. Interested in 2BHK, campus visit, appointment, callback..."
                    className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </label>
                <label className="block md:col-span-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Notes</span>
                  <textarea
                    rows={3}
                    value={manualLead.notes}
                    onChange={(e) => setManualLead({ ...manualLead, notes: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </label>
                <label className="md:col-span-2 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={manualLead.allow_duplicate}
                    onChange={(e) => setManualLead({ ...manualLead, allow_duplicate: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  Allow duplicate email or phone for this tenant
                </label>
              </div>

              <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowManualLeadModal(false)}
                  className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingManualLead || Boolean(manualPhoneProblem)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
                >
                  {savingManualLead ? 'Saving...' : 'Save Lead'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CSV Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full">
            <form onSubmit={handleImportCsv}>
              <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Import Leads CSV</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Tenant admins can import up to 1000 historical leads at a time.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowImportModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4 text-sm text-blue-900 dark:text-blue-200">
                  Supported headers include name, email, phone, company, status, source, created_at, request_type,
                  priority_score, conversation_id, user_query, notes, and assigned_to. Duplicate email/phone values
                  in the same tenant are skipped.
                </div>
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-gray-700 dark:text-gray-300 file:mr-4 file:rounded-lg file:border-0 file:bg-indigo-600 file:px-4 file:py-2 file:text-white hover:file:bg-indigo-700"
                />
                {importResult && (
                  <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 text-sm text-gray-700 dark:text-gray-300">
                    <p className="font-semibold text-gray-900 dark:text-white">{importResult.message}</p>
                    <p>Created: {importResult.created || 0}</p>
                    <p>Skipped duplicates: {importResult.skipped?.length || 0}</p>
                    <p>Errors: {importResult.errors?.length || 0}</p>
                    {importResult.errors?.length > 0 && (
                      <ul className="mt-2 list-disc pl-5">
                        {importResult.errors.slice(0, 5).map((item, index) => (
                          <li key={index}>Row {item.row}: {String(item.error)}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>

              <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowImportModal(false)}
                  className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900"
                >
                  Close
                </button>
                <button
                  type="submit"
                  disabled={importingLeads || !csvFile}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-60"
                >
                  {importingLeads ? 'Importing...' : 'Import Leads'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lead Detail Modal */}
      {showLeadModal && selectedLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Lead Details</h3>
              <button
                onClick={() => setShowLeadModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Contact Info */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between border-b pb-2">
                    <h4 className="font-semibold text-gray-900 dark:text-white">Contact Information</h4>
                    {!editingContact ? (
                      <button
                        onClick={startEditContact}
                        className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        Edit
                      </button>
                    ) : (
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => { setEditingContact(false); setContactError(null); }}
                          disabled={savingContact}
                          className="text-sm text-gray-500 dark:text-gray-400 hover:underline disabled:opacity-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveContact}
                          disabled={savingContact}
                          className="text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded px-3 py-1 disabled:opacity-50"
                        >
                          {savingContact ? 'Saving…' : 'Save'}
                        </button>
                      </div>
                    )}
                  </div>
                  {contactError && (
                    <p className="text-sm text-red-600 dark:text-red-400">{contactError}</p>
                  )}
                  {!editingContact ? (
                    <div className="space-y-2">
                      <p className="flex items-center gap-2">
                        <UserGroupIcon className="h-5 w-5 text-gray-400" />
                        <span className="text-gray-600 dark:text-gray-400">Name:</span>
                        <span className="text-gray-900 dark:text-white">{selectedLead.name || 'N/A'}</span>
                      </p>
                      <p className="flex items-center gap-2">
                        <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                        <span className="text-gray-600 dark:text-gray-400">Email:</span>
                        <span className="text-gray-900 dark:text-white">{selectedLead.email || 'N/A'}</span>
                      </p>
                      <p className="flex items-center gap-2">
                        <PhoneIcon className="h-5 w-5 text-gray-400" />
                        <span className="text-gray-600 dark:text-gray-400">Phone:</span>
                        <span className="text-gray-900 dark:text-white">{selectedLead.phone || 'N/A'}</span>
                      </p>
                      <p className="flex items-center gap-2">
                        <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
                        <span className="text-gray-600 dark:text-gray-400">Company:</span>
                        <span className="text-gray-900 dark:text-white">{selectedLead.company || 'N/A'}</span>
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {[
                        { key: 'name', label: 'Name', icon: UserGroupIcon, type: 'text', placeholder: 'Full name' },
                        { key: 'email', label: 'Email', icon: EnvelopeIcon, type: 'email', placeholder: 'name@company.com' },
                        { key: 'phone', label: 'Phone', icon: PhoneIcon, type: 'tel', placeholder: 'Phone number' },
                        { key: 'company', label: 'Company', icon: BuildingOfficeIcon, type: 'text', placeholder: 'Company' },
                      ].map((f) => (
                        <label key={f.key} className="flex items-center gap-2">
                          <f.icon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                          <span className="w-16 text-sm text-gray-600 dark:text-gray-400 flex-shrink-0">{f.label}</span>
                          <input
                            type={f.type}
                            value={contactDraft[f.key]}
                            placeholder={f.placeholder}
                            onChange={(e) => setContactDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                            className="flex-1 px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                          />
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* Chat Info */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white border-b pb-2">Chat Information</h4>
                  <div className="space-y-2">
                    <p>
                      <span className="text-gray-600 dark:text-gray-400">Agent:</span>
                      <span className="text-gray-900 dark:text-white ml-2">{selectedLead.agent_name || 'N/A'}</span>
                    </p>
                    <p>
                      <span className="text-gray-600 dark:text-gray-400">Messages:</span>
                      <span className="text-gray-900 dark:text-white ml-2">{selectedLead.messages_count}</span>
                    </p>
                    <p>
                      <span className="text-gray-600 dark:text-gray-400">Source:</span>
                      <span className="text-gray-900 dark:text-white ml-2">{selectedLead.source || 'N/A'}</span>
                    </p>
                    {(selectedLead.campaign_source || selectedLead.campaign_name) && (
                      <div className="space-y-2 rounded-lg border border-cyan-100 bg-cyan-50/60 p-3 dark:border-cyan-900/40 dark:bg-cyan-900/20">
                        <p className="text-sm font-semibold text-cyan-800 dark:text-cyan-200">Campaign Attribution</p>
                        <p>
                          <span className="text-gray-600 dark:text-gray-400">Campaign Source:</span>
                          <span className="text-gray-900 dark:text-white ml-2">{selectedLead.campaign_source || 'N/A'}</span>
                        </p>
                        <p>
                          <span className="text-gray-600 dark:text-gray-400">Campaign Name:</span>
                          <span className="text-gray-900 dark:text-white ml-2">{selectedLead.campaign_name || 'N/A'}</span>
                        </p>
                      </div>
                    )}
                    {selectedLead.custom_fields?.lead_source && (
                      <p>
                        <span className="text-gray-600 dark:text-gray-400">Lead Source:</span>
                        <span className="text-gray-900 dark:text-white ml-2">
                          {selectedLead.custom_fields.lead_source.replace(/_/g, ' ')}
                        </span>
                      </p>
                    )}
                    {selectedLead.custom_fields?.original_lead_date && (
                      <p>
                        <span className="text-gray-600 dark:text-gray-400">Original Lead Date:</span>
                        <span className="text-gray-900 dark:text-white ml-2">
                          {selectedLead.custom_fields.original_lead_date}
                        </span>
                      </p>
                    )}
                    <p>
                      <span className="text-gray-600 dark:text-gray-400">Status:</span>
                      <span className={`ml-2 px-2 py-1 rounded-full text-xs ${statusColors[selectedLead.status]}`}>
                        {formatLeadStatus(selectedLead.status)}
                      </span>
                    </p>
                    {selectedLead.custom_fields?.request_type && (
                      <p>
                        <span className="text-gray-600 dark:text-gray-400">Request:</span>
                        <span className="ml-2 px-2 py-1 rounded-full text-xs bg-indigo-50 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300">
                          {selectedLead.custom_fields.request_label || requestTypeLabels[selectedLead.custom_fields.request_type] || selectedLead.custom_fields.request_type.replace(/_/g, ' ')}
                        </span>
                      </p>
                    )}
                    {selectedLead.custom_fields?.preferred_datetime && (
                      <p>
                        <span className="text-gray-600 dark:text-gray-400">Preferred Time:</span>
                        <span className="text-gray-900 dark:text-white ml-2">
                          {new Date(selectedLead.custom_fields.preferred_datetime).toLocaleString()}
                        </span>
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Priority Analysis */}
              <div className="mt-6">
                <h4 className="font-semibold text-gray-900 dark:text-white border-b pb-2 mb-3">Priority Analysis</h4>
                {selectedLead.priority_tier ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1 rounded-full ${priorityStyles[selectedLead.priority_tier]?.bg || ''}`}>
                        <span className={`h-2 w-2 rounded-full ${priorityStyles[selectedLead.priority_tier]?.dot || ''}`} />
                        {priorityStyles[selectedLead.priority_tier]?.label || selectedLead.priority_tier} — {selectedLead.priority_score}/100
                      </span>
                      {selectedLead.scored_at && (
                        <span className="text-xs text-gray-400">
                          Scored {new Date(selectedLead.scored_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    {selectedLead.priority_signals && selectedLead.priority_signals.length > 0 && (
                      <ul className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
                        {selectedLead.priority_signals.map((s, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="text-green-500 mt-0.5">&#10003;</span>
                            {s}
                          </li>
                        ))}
                      </ul>
                    )}
                    <button
                      onClick={async () => {
                        try {
                          const res = await analyticsAPI.rescoreLead(selectedLead.id);
                          if (res.data?.lead) setSelectedLead(res.data.lead);
                          loadData();
                        } catch (e) {
                          console.error('Rescore failed', e);
                        }
                      }}
                      className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 hover:underline"
                    >
                      Re-score
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-400">Not yet scored</span>
                    <button
                      onClick={async () => {
                        try {
                          const res = await analyticsAPI.rescoreLead(selectedLead.id);
                          if (res.data?.lead) setSelectedLead(res.data.lead);
                          loadData();
                        } catch (e) {
                          console.error('Rescore failed', e);
                        }
                      }}
                      className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 hover:underline"
                    >
                      Score now
                    </button>
                  </div>
                )}
              </div>

              {/* User Query */}
              {selectedLead.user_query && (
                <div className="mt-6">
                  <h4 className="font-semibold text-gray-900 dark:text-white border-b pb-2 mb-2">Initial Query</h4>
                  <p className="text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    {selectedLead.user_query}
                  </p>
                </div>
              )}

              {/* Chat Summary */}
              {selectedLead.chat_summary && (
                <div className="mt-6">
                  <h4 className="font-semibold text-gray-900 dark:text-white border-b pb-2 mb-2">Chat Summary</h4>
                  <p className="text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    {selectedLead.chat_summary}
                  </p>
                </div>
              )}

              {/* Chat History */}
              {selectedLead.chat_history && selectedLead.chat_history.length > 0 && (
                <div className="mt-6">
                  <h4 className="font-semibold text-gray-900 dark:text-white border-b pb-2 mb-2">
                    Chat History ({selectedLead.chat_history.length} messages)
                  </h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {selectedLead.chat_history.map((msg, idx) => (
                      <div
                        key={idx}
                        className={`p-3 rounded-lg ${
                          msg.role === 'user'
                            ? 'bg-blue-50 dark:bg-blue-900/30 ml-8'
                            : 'bg-gray-50 dark:bg-gray-700 mr-8'
                        }`}
                      >
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                          {msg.role === 'user' ? 'User' : 'Assistant'}
                        </p>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{msg.content}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* WhatsApp / BSP Activity */}
              <div className="mt-6">
                <h4 className="font-semibold text-gray-900 dark:text-white border-b pb-2 mb-3">
                  Channel messages
                  {!bspConvosLoading && (
                    <span className="ml-2 text-xs font-normal text-gray-500">({bspConvos.length})</span>
                  )}
                </h4>
                {bspConvosLoading ? (
                  <p className="text-sm text-gray-400">Loading WhatsApp conversations…</p>
                ) : bspConvos.length === 0 ? (
                  <p className="text-sm text-gray-400">
                    No WhatsApp or Instagram messages linked to this lead yet. Customer conversations appear in the Customer inbox; admins manage connections in Channels.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {bspConvos.map((convo) => {
                      const isExpanded = expandedBspConvoId === convo.id;
                      return (
                        <div key={convo.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                          <button
                            type="button"
                            onClick={() => handleExpandBspConvo(convo.id)}
                            className="w-full text-left px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            <div className="flex flex-col">
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {convo.provider?.toUpperCase() || 'BSP'} · {convo.contact_name || convo.contact_phone || 'Contact'}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                Status: {convo.status || 'active'}
                                {convo.last_message_at && ` · Last: ${new Date(convo.last_message_at).toLocaleString()}`}
                              </span>
                            </div>
                            <span className="text-xs text-indigo-600 dark:text-indigo-400">
                              {isExpanded ? 'Hide' : 'View thread'}
                            </span>
                          </button>
                          {isExpanded && (
                            <div className="border-t border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-800">
                              {bspMessagesLoading ? (
                                <p className="text-xs text-gray-400">Loading messages…</p>
                              ) : bspMessages.length === 0 ? (
                                <p className="text-xs text-gray-400">No messages stored.</p>
                              ) : (
                                <div className="space-y-2 max-h-60 overflow-y-auto">
                                  {bspMessages.map((msg) => (
                                    <div
                                      key={msg.id}
                                      className={`p-2 rounded text-sm ${
                                        msg.direction === 'inbound'
                                          ? 'bg-blue-50 dark:bg-blue-900/30 mr-8'
                                          : 'bg-green-50 dark:bg-green-900/30 ml-8'
                                      }`}
                                    >
                                      <p className="text-[10px] text-gray-500 dark:text-gray-400 mb-0.5">
                                        {msg.sender_type || (msg.direction === 'inbound' ? 'customer' : 'human')}
                                        {msg.occurred_at && ` · ${new Date(msg.occurred_at).toLocaleString()}`}
                                      </p>
                                      <p className="text-gray-700 dark:text-gray-200 whitespace-pre-wrap">
                                        {msg.content || `[${msg.message_type}]`}
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Assignment */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white border-b pb-2 mb-3">Assigned To</h4>
                  {isTenantAdmin ? (
                    <select
                      value={selectedLead.assigned_to || ''}
                      onChange={(e) => handleAssignLead(selectedLead.id, e.target.value)}
                      className="w-full text-sm px-3 py-2 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">Unassigned</option>
                      {teamMembers.map((member) => (
                        <option key={member.id} value={member.id}>
                          {member.full_name || member.email || member.username || 'Unknown'}
                        </option>
                      ))}
                    </select>
                  ) : (
                    // Non-admin tenant members see assignment as read-only text.
                    // Mirrors the row-level dropdown gate above; backend also 403s.
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      {(() => {
                        const owner = teamMembers.find(m => m.id === selectedLead.assigned_to);
                        return owner
                          ? (owner.full_name || owner.email || owner.username || 'Assigned')
                          : 'Unassigned';
                      })()}
                    </p>
                  )}
                  {selectedLead.assigned_at && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      Assigned on {new Date(selectedLead.assigned_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>

              {/* Notes */}
              <div className="mt-6">
                <div className="flex items-center justify-between border-b pb-2 mb-3">
                  <h4 className="font-semibold text-gray-900 dark:text-white">Notes</h4>
                  <button
                    onClick={openWhatsAppNote}
                    className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded border ${
                      quickWhatsAppNoteEnabled
                        ? 'border-green-500 text-green-700 dark:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/30'
                        : 'border-gray-300 text-gray-400 cursor-help opacity-60'
                    }`}
                    title={quickWhatsAppNoteEnabled ? 'Stamp a WhatsApp conversation outcome' : 'Available on the Growth plan — talk to sales to upgrade'}
                  >
                    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="currentColor">
                      <path d="M17.5 14.4c-.3-.15-1.8-.9-2.05-1-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.27-.47-2.4-1.5-.9-.8-1.5-1.78-1.67-2.08-.17-.3-.02-.46.13-.6.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.5l-.57-.01c-.2 0-.52.07-.8.37-.27.3-1.05 1.02-1.05 2.5 0 1.47 1.07 2.9 1.22 3.1.15.2 2.1 3.2 5.08 4.48.71.3 1.26.49 1.69.63.71.22 1.36.2 1.87.12.57-.08 1.76-.72 2-1.4.25-.7.25-1.28.18-1.4-.07-.13-.27-.2-.57-.35zM12 2a10 10 0 0 0-8.6 15.06L2 22l5.06-1.33A10 10 0 1 0 12 2z"/>
                    </svg>
                    Just had a WhatsApp conversation
                  </button>
                </div>
                <textarea
                  className="w-full text-sm px-3 py-2 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white resize-none"
                  rows={3}
                  placeholder="Add a note about this lead..."
                  value={notesDraft}
                  onChange={(e) => setNotesDraft(e.target.value)}
                />
                <button
                  onClick={handleSaveNote}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 hover:underline"
                >
                  Save Note
                </button>
              </div>

              {/* Timestamps */}
              <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-500 dark:text-gray-400">
                <p>Created: {formatDate(selectedLead.created_at)}</p>
                <p>Last Updated: {formatDate(selectedLead.updated_at)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick WhatsApp Note modal — mobile-friendly, 3 fields, 1-tap save */}
      {showWhatsAppNote && selectedLead && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-end sm:items-center justify-center z-[60] sm:p-4">
          <div className="bg-white dark:bg-gray-800 w-full sm:max-w-md sm:rounded-lg shadow-xl rounded-t-2xl max-h-[95vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <svg viewBox="0 0 24 24" className="h-5 w-5 text-green-600" fill="currentColor">
                  <path d="M17.5 14.4c-.3-.15-1.8-.9-2.05-1-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.27-.47-2.4-1.5-.9-.8-1.5-1.78-1.67-2.08-.17-.3-.02-.46.13-.6.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.5l-.57-.01c-.2 0-.52.07-.8.37-.27.3-1.05 1.02-1.05 2.5 0 1.47 1.07 2.9 1.22 3.1.15.2 2.1 3.2 5.08 4.48.71.3 1.26.49 1.69.63.71.22 1.36.2 1.87.12.57-.08 1.76-.72 2-1.4.25-.7.25-1.28.18-1.4-.07-.13-.27-.2-.57-.35zM12 2a10 10 0 0 0-8.6 15.06L2 22l5.06-1.33A10 10 0 1 0 12 2z"/>
                </svg>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">Quick WhatsApp Note</h3>
              </div>
              <button
                onClick={() => setShowWhatsAppNote(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto flex-1 space-y-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Stamping a conversation for <span className="font-medium text-gray-700 dark:text-gray-200">{selectedLead.name || 'this lead'}</span>. Prepended to Notes with timestamp.
              </p>

              {/* 1. Outcome quick-pick (3 big buttons, touch-friendly) */}
              <div>
                <label className="block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Outcome</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'Qualified', status: 'qualified', color: 'green' },
                    { value: 'Booked', status: 'converted', color: 'purple' },
                    { value: 'Lost', status: 'lost', color: 'red' },
                  ].map((opt) => {
                    const active = waNoteOutcome === opt.value;
                    const base = 'py-3 rounded-lg text-sm font-medium border transition';
                    const colorClass = active
                      ? `bg-${opt.color}-600 text-white border-${opt.color}-600`
                      : `bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-${opt.color}-50 dark:hover:bg-${opt.color}-900/30`;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => {
                          setWaNoteOutcome(opt.value);
                          setWaNoteStatus(opt.status);
                        }}
                        className={`${base} ${colorClass}`}
                      >
                        {opt.value}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* 2. Status override (full enum, defaults from outcome) */}
              <div>
                <label htmlFor="wa-note-status" className="block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Status</label>
                <select
                  id="wa-note-status"
                  value={waNoteStatus}
                  onChange={(e) => setWaNoteStatus(e.target.value)}
                  className="w-full text-sm px-3 py-2.5 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  {leadStatusOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* 3. Summary (required) */}
              <div>
                <label htmlFor="wa-note-summary" className="block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                  What did they say? <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="wa-note-summary"
                  className="w-full text-sm px-3 py-2 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white resize-none"
                  rows={4}
                  placeholder="e.g. Asked for 2BHK site visit Sat 4pm, budget 80L, will bring spouse."
                  value={waNoteSummary}
                  onChange={(e) => setWaNoteSummary(e.target.value)}
                  autoFocus
                />
              </div>
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 p-3 flex gap-2">
              <button
                type="button"
                onClick={() => setShowWhatsAppNote(false)}
                className="flex-1 sm:flex-none px-4 py-2.5 rounded text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveWhatsAppNote}
                disabled={waNoteSaving || !waNoteSummary.trim()}
                className="flex-1 px-4 py-2.5 rounded text-sm font-medium bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {waNoteSaving ? 'Saving…' : 'Save & Update Lead'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setItemToDelete(null); }}
        onConfirm={() => handleDeleteLead(itemToDelete?.id)}
        title="Delete Lead"
        entityName={itemToDelete?.name || itemToDelete?.email || 'Anonymous'}
        warningText="This will permanently delete this lead and all associated data. This action cannot be undone."
        confirmButtonText="Delete Lead"
      />
    </div>
  );
};

export default AnalyticsPage;
